/**
 *
 * AssessmentItemSmall
 *
 */

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import _get from 'lodash/get';
import _has from 'lodash/has';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import ButtonBase from '@material-ui/core/ButtonBase';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeSelectAuth } from 'containers/Auth/selectors';
import { trackFavoriteAction } from 'containers/Favorites/utils';
import {
  makeSelectAllowSignIn,
  makeSelectClientDetails,
  makeSelectTextDirection,
} from 'containers/Main/selectors';
import Mixpanel from 'utils/mixpanelService';
import UserInteractionService from 'utils/userInteractionService';
import { getAssessmentName } from 'containers/Assessment/utils';
import { saveAssessment } from 'containers/Assessments/actions';
import {
  getColorsConfig,
  isAssessmentSaved,
  isAssessmentTaken,
} from 'containers/Assessments/utils';
import {
  makeSelectSavedAssessments,
  makeSelectTakenAssessments,
} from 'containers/Assessments/selectors';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useWindowSize from 'components/useWindowSize';
import { getAlgoliaLocalizedField } from 'utils/localeUtils';
import { isBot, getImageFile } from 'utils/stringUtils';
import {
  getEventPageName,
  getPathSegment,
  getSlugSegment,
} from 'utils/trackingPageUtils';
import NewResourceLabel from 'components/NewResourceLabel';
import { SaveFavoriteButton } from '../SaveFavoriteButton';

const useStyles = makeStyles(theme => ({
  wrapper: {
    position: 'relative',
    cursor: 'pointer',
    maxWidth: props => props.fromSuggestions && 175,
  },
  openWrapper: {
    width: '200%',
    zIndex: 8,
    left: '-50%',
  },
  rightWrapper: {
    left: 0,
  },
  leftWrapper: {
    left: '-100%',
  },
  dataWrapper: {
    position: 'relative',
    height: '100%',
  },
  container: {
    overflow: 'hidden',
    borderRadius: props => `${props.fromSuggestions ? 10 : 5}px !important`,
    border: `1px solid #E9ECED`,
    height: props => !props.fromSuggestions && 271,
    width: 'auto',
    position: 'relative',
  },
  menuCardContainer: {
    height: 318,
    width: 260,
  },
  background: {
    display: 'flex',
    flexFlow: 'column',
    height: '100%',
    width: '100%',
  },
  fullHeight: {
    height: '100%',
  },
  duration: {
    position: 'absolute',
    height: 21,
    left: 10,
    top: 9,
    paddingLeft: 10,
    paddingRight: 10,
    zIndex: 2,
    backgroundColor: theme.palette.primary.main,
    ...theme.typography.pxSmall,
    color: '#FFFFFF',
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      left: 6,
      paddingTop: 2,
      fontSize: '0.65rem',
    },
  },
  iconButton: {
    position: 'static',
    display: 'flex',
    padding: 4,
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    '&:hover': {
      backgroundColor: 'inherit',
    },
  },
  openContentWrapper: {
    '&:first-child': {
      borderRight: '1px solid transparent',
    },
    '&:last-child': {
      borderLeft: '1px solid transparent',
    },
  },
  icon: {
    width: '100%',
    height: '100%',
    padding: 0,
    borderRadius: 0,
    backgroundColor: 'transparent',
    '&:hover': {
      background: 'none',
    },
  },
  infoWrapper: {
    backgroundColor: ({ color, useClientImage }) =>
      useClientImage ? 'inherit' : color.main,
    padding: '12px 10px',
    width: '100%',
    color: ({ color }) => color.font,
  },
  type: {
    textAlign: 'left',
    color: 'inherit',
  },
  assessmentIcon: {
    width: 12,
    height: 12,
    marginRight: 4,
  },
  name: {
    display: 'flex',
    ...theme.typography.h2,
    fontSize: '0.875rem',
    color: ({ color }) => color.font,
    direction: ({ textDirection }) => textDirection,
    justifyContent: ({ textDirection }) =>
      textDirection === 'rtl' ? 'flex-end' : 'flex-start',
  },
  takeAction: {
    width: 'fit-content',
    height: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    ...theme.typography.button,
    fontSize: '0.75rem',
  },
  takeArrow: {
    marginLeft: 4,
  },
  imageWrapper: {
    flex: 1,
    overflowY: 'auto',
    position: 'relative',
  },
  takenLabel: {
    ...theme.typography.pSmallBold,
    fontSize: '0.75rem',
    position: 'absolute',
    right: 11,
    bottom: 13,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#FFFFFF',
    borderRadius: 2,
    textTransform: 'initial',
    padding: '3px 9px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 1)',
    },
  },
  assessmentImage: {
    height: '100%',
    width: '100%',
    maxHeight: ({ fromSuggestions }) => fromSuggestions && 135,
    objectFit: ({ withObjectFit, fromSuggestions }) =>
      (withObjectFit || fromSuggestions) && 'cover',
  },
  grayscale: {
    filter: 'grayscale(1)',
  },
  headerWrapper: {
    position: 'absolute',
    top: 5,
    right: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
    zIndex: 3,
    [theme.breakpoints.down('sm')]: {
      right: 0,
      gap: 0,
    },
  },
  headerBadgeOverride: {
    position: 'static !important',
    top: 'auto !important',
    right: 'auto !important',
  },
}));

const getImageData = ({ item, useAltImage, useClientImage }) => {
  const isAlgoliaItem = !!item.objectID;
  if (isAlgoliaItem) {
    return {
      imageUrl: useAltImage ? item.altImageUrl || item.imageUrl : item.imageUrl,
    };
  }
  const clientImage = useClientImage ? _get(item, 'clientImage.0') : null;

  return {
    image:
      useClientImage && clientImage
        ? clientImage
        : useAltImage
        ? item.altImage || item.image
        : item.image,
  };
};

const OBJECT_FIT_RATIO = 1.9;
const ALT_IMAGE_PERCENTAGE_RATIO = 5;

const AssessmentItemSmall = ({
  item,
  item: { time, slug, name = '', nameTitle, customNames, callToAction = '' },
  listTitle,
  onClick,
  fromSuggestions,
  recommendation,
  expanded,
  menuCard,
  customClasses,
  grayscale,
  useClientImage,
  durationAltPosition = false,
  trackingProps = {},
  onRedirect,
}) => {
  const [open] = useState(expanded);
  const hasTracked = useRef(false);
  const wrapperRef = useRef();
  const imageWrapperRef = useRef();
  const dispatch = useDispatch();
  const savedAssessments = useSelector(makeSelectSavedAssessments());
  const takenAssessments = useSelector(makeSelectTakenAssessments());
  const textDirection = useSelector(makeSelectTextDirection());
  const clientDetails = useSelector(makeSelectClientDetails());
  const history = useHistory();
  const pageName = getPathSegment();
  const auth = useSelector(makeSelectAuth());
  const allowSignIn = useSelector(makeSelectAllowSignIn());
  const locale = useAlgoliaLocale();
  const [
    resourceItemPageSiteCopy,
    allAssessmentsSiteCopy,
  ] = useSiteCopySelector(['resource-item-page', 'all-assessments']);

  const windowSize = useWindowSize();
  const [{ useAltImage, withObjectFit }, setImageSettings] = useState({
    useAltImage: true,
    withObjectFit: true,
  });

  const { color, takeArrowSvg, checkSvg } = getColorsConfig(item);
  const imageData = getImageData({ item, useAltImage, useClientImage });
  const imageUrl = getImageFile(imageData);

  const classes = useStyles({
    color,
    imageUrl,
    withObjectFit,
    useClientImage,
    textDirection,
    fromSuggestions,
  });

  const saved = isAssessmentSaved(savedAssessments, {
    id: item.objectID || item.id,
  });
  const taken = isAssessmentTaken(takenAssessments, {
    id: item.objectID || item.id,
  });

  useEffect(() => {
    const imageWrapper = imageWrapperRef.current;
    if (!imageWrapper) {
      return;
    }

    const width = imageWrapper.clientWidth;
    const height = imageWrapper.clientHeight;
    const imageRatio = width / height;
    const isAltImage = (imageRatio - 1) * 100 >= ALT_IMAGE_PERCENTAGE_RATIO;
    const isObjectFit = imageRatio < OBJECT_FIT_RATIO;

    setImageSettings({
      useAltImage: isAltImage,
      withObjectFit: isObjectFit,
    });
  }, [windowSize]);

  const handleSave = e => {
    e.preventDefault();
    e.stopPropagation();

    const id = item.objectID || item.id;

    let nodeID;
    if (saved) [{ nodeID }] = savedAssessments.filter(el => el.sys.id === id);

    const payload = {
      nodeID,
      assessment: {
        assessmentID: id,
        slug,
        name,
        callToAction,
        time,
      },
      save: !saved,
    };

    const finalPageName = `${pageName} Page`;
    trackFavoriteAction(
      'Card Click',
      {
        page: finalPageName,
        type: 'Assessments',
        resourceID: id,
        slug,
        name,
      },
      auth,
      saved,
    );
    dispatch(saveAssessment(payload));
  };

  const redirect = (event, isTaken) => {
    const finalPageName = getEventPageName();

    // For menu cards, track "Menu Item - Clicked" instead
    const trackingEvent = menuCard ? 'Menu Item - Clicked' : event;
    const trackingData = menuCard
      ? {
          type: 'Assessments',
          name,
          section: trackingProps.section || 'featured',
          page: finalPageName,
          ...trackingProps,
        }
      : {
          name,
          type: 'assessment',
          page: finalPageName,
          listTitle,
          path: getPathSegment(),
          slug: getSlugSegment(),
          entryId: item.objectID || item.id,
          taken: isTaken,
          recommendation,
          ...trackingProps,
        };

    Mixpanel.track(trackingEvent, trackingData);

    const redirectUrl =
      event === 'Get Your Results' && isTaken
        ? `/assessments/${slug}/results`
        : `/assessments/${slug}`;

    if (onClick) {
      onClick({
        type: 'Assessments',
        name: nameTitle,
        entryId: item.objectID || item.id,
      });
    }
    if (onRedirect) {
      onRedirect(redirectUrl);
    } else {
      history.push(redirectUrl);
    }
  };

  const handleCardClick = e => {
    e.preventDefault();
    e.stopPropagation();

    redirect('Resource Card - Clicked', taken);
  };

  const handleTakeAssessment = e => {
    e.preventDefault();
    e.stopPropagation();

    redirect('Take Assessment', taken);
  };

  const handleTakenButton = e => {
    e.preventDefault();
    e.stopPropagation();

    redirect('Get Your Results', taken);
  };

  const duration = `${time} ${
    time < 2
      ? _get(allAssessmentsSiteCopy, 'pageCopy.minSingular', 'MIN')
      : _get(allAssessmentsSiteCopy, 'pageCopy.minPlural', 'MINS')
  }`;

  const bot = isBot();

  const resourceAudiences = [];
  _get(item, 'audienceType', _get(item, 'audience', [])).forEach(entry => {
    if (
      _has(entry, 'fields.reviewStatus') &&
      _get(entry, 'fields.reviewStatus') !== 'Accepted'
    )
      return;

    const audienceName = _get(entry, 'fields.name', entry);
    resourceAudiences.push(audienceName);
  });
  const resourceTags = [];
  _get(item, 'fields.tags', _get(item, 'audience', [])).forEach(entry => {
    if (
      _has(entry, 'fields.reviewStatus') &&
      _get(entry, 'fields.reviewStatus') !== 'Accepted'
    )
      return;

    const tagTitle = _get(entry, 'fields.title', entry);
    resourceTags.push(tagTitle);
  });

  const shouldShowSave = !menuCard && !bot && allowSignIn && !fromSuggestions;

  const customNamesConfig = _has(customNames, 'config')
    ? _get(customNames, 'config', [])
    : customNames;
  const finalName = getAssessmentName(
    getAlgoliaLocalizedField(item, 'name', locale) ||
      getAlgoliaLocalizedField(item, 'nameTitle', locale),
    customNamesConfig,
    clientDetails.shortName,
  );

  const takeLabel = _get(resourceItemPageSiteCopy, [
    'pageCopy',
    taken ? 'retakeAssessmentLabel' : 'takeAssessmentLabel',
  ]);

  const viewResult = _get(
    resourceItemPageSiteCopy,
    'pageCopy.takenAssessmentLabel',
  );

  return (
    <div
      className={_classNames(classes.wrapper, customClasses?.root, {
        [classes.openWrapper]: !expanded && open,
      })}
      ref={wrapperRef}
    >
      <Box onClick={handleCardClick}>
        <Paper
          elevation={0}
          className={_classNames(classes.container, {
            [classes.menuCardContainer]: menuCard,
          })}
          onMouseEnter={() => {
            if (!hasTracked.current) {
              hasTracked.current = true;
              UserInteractionService.registerGeneralInteraction({
                type: 'resource-card-hovered',
                resourceID: item.objectID || item.id,
                resourceAudiences: resourceAudiences.join(),
                resourceTags: null,
                resourceType: 'assessment',
              });
            }
          }}
        >
          <div className={classes.headerWrapper}>
            {!fromSuggestions && (
              <NewResourceLabel
                resource={item}
                type="assessment"
                className={classes.headerBadgeOverride}
              />
            )}
            {shouldShowSave && (
              <SaveFavoriteButton
                saved={saved}
                title={finalName}
                classes={classes}
                handleSave={handleSave}
              />
            )}
          </div>
          <Grid container className={classes.fullHeight}>
            <Grid item xs={12} className={_classNames(classes.dataWrapper)}>
              {!durationAltPosition && (
                <Typography className={classes.duration}>{duration}</Typography>
              )}
              <div className={classes.background}>
                <div className={classes.imageWrapper} ref={imageWrapperRef}>
                  <img
                    src={imageUrl}
                    className={_classNames({
                      [classes.assessmentImage]: true,
                      [classes.grayscale]: grayscale,
                    })}
                    alt=""
                  />
                  {taken && (
                    <Button
                      variant="text"
                      component="span"
                      onClick={handleTakenButton}
                      role="link"
                      aria-label={`${finalName} ${viewResult}`}
                      className={_classNames(classes.takenLabel, {
                        [classes.grayscale]: grayscale,
                      })}
                    >
                      {viewResult}
                      <img
                        src={takeArrowSvg}
                        alt=""
                        className={classes.takeArrow}
                      />
                    </Button>
                  )}
                </div>
                <div className={classes.infoWrapper}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography
                        variant="overline"
                        component="p"
                        className={classes.type}
                      >
                        <img
                          src={checkSvg}
                          alt=""
                          className={classes.assessmentIcon}
                        />
                        {_get(
                          resourceItemPageSiteCopy,
                          'pageCopy.resourceCardTypeMapping.Assessment',
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="body1"
                        className={classes.name}
                        gutterBottom
                        id={`take-description-${item.id}`}
                        align="left"
                      >
                        {finalName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <ButtonBase
                        variant="text"
                        disableRipple
                        component="p"
                        role="link"
                        onClick={handleTakeAssessment}
                        aria-label={`${takeLabel} ${finalName}`}
                        classes={{
                          root: classes.takeAction,
                        }}
                      >
                        {takeLabel}
                        {durationAltPosition && ` (${duration})`}
                        <img
                          src={takeArrowSvg}
                          alt=""
                          className={classes.takeArrow}
                        />
                      </ButtonBase>
                    </Grid>
                  </Grid>
                </div>
              </div>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </div>
  );
};
AssessmentItemSmall.propTypes = {
  item: PropTypes.object,
  recommendation: PropTypes.bool,
  expanded: PropTypes.bool,
  listTitle: PropTypes.string,
  menuCard: PropTypes.bool,
  grayscale: PropTypes.bool,
  onClick: PropTypes.func,
  fromSuggestions: PropTypes.bool,
  useClientImage: PropTypes.bool,
};

AssessmentItemSmall.defaultProps = {
  recommendation: false,
  expanded: false,
  menuCard: false,
};

export default AssessmentItemSmall;
